import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { mediaUrl, API_BASE, getToken } from "@/lib/api";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/admin/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!res.ok) throw new Error("Upload failed");
  return res.json();
}

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/projects");
    setProjects(data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/admin/projects", {
      title: "New Project",
      slug: `new-project-${Date.now().toString(36)}`,
      category_slug: "portraits",
      gallery: [],
      published: false,
      order: 99,
    });
    setSelected(data);
    load();
  };

  const save = async () => {
    if (!selected) return;
    try {
      const body = { ...selected };
      if (typeof body.gallery === "string") {
        body.gallery = body.gallery.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      const { data } = await api.patch(`/admin/projects/${selected.id}`, body);
      toast.success("Project saved");
      setSelected(data);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async () => {
    if (!selected || !window.confirm(`Delete ${selected.title}?`)) return;
    await api.delete(`/admin/projects/${selected.id}`);
    setSelected(null);
    load();
  };

  const onGalleryFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const { url } = await uploadFile(f);
        uploaded.push(url);
      }
      setSelected({ ...selected, gallery: [...(selected.gallery || []), ...uploaded] });
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onCoverFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadFile(file);
      setSelected({ ...selected, cover_image: url });
      toast.success("Cover uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImg = (idx) => {
    const arr = [...(selected.gallery || [])];
    arr.splice(idx, 1);
    setSelected({ ...selected, gallery: arr });
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-end justify-between">
        <div>
          <p className="label-mono opacity-60">Portfolio</p>
          <h1 className="mt-2 font-editorial text-5xl md:text-6xl">Projects</h1>
        </div>
        <button onClick={create} className="btn-outline-ink" data-testid="project-new-btn">+ New Project</button>
      </div>

      <div className="mt-10 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-5 border border-ink/15 bg-white divide-y divide-ink/10 max-h-[70vh] overflow-y-auto">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              className={`w-full text-left p-4 hover:bg-ink/5 ${selected?.id === p.id ? "bg-ink/5" : ""}`}
              data-testid={`project-row-${p.id}`}
            >
              <p className="font-editorial text-lg">{p.title}</p>
              <p className="label-mono opacity-60 mt-1">{p.category_slug} · {p.published ? "Published" : "Draft"}</p>
            </button>
          ))}
        </div>
        <div className="col-span-12 md:col-span-7 border border-ink/15 bg-white p-6">
          {selected ? (
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-mono opacity-60">Title</label>
                  <input className="editorial-input" value={selected.title || ""} onChange={(e) => setSelected({ ...selected, title: e.target.value })} data-testid="project-title" />
                </div>
                <div>
                  <label className="label-mono opacity-60">Slug</label>
                  <input className="editorial-input" value={selected.slug || ""} onChange={(e) => setSelected({ ...selected, slug: e.target.value })} />
                </div>
                <div>
                  <label className="label-mono opacity-60">Category</label>
                  <select className="editorial-select" value={selected.category_slug} onChange={(e) => setSelected({ ...selected, category_slug: e.target.value })}>
                    <option value="portraits">Portraits</option>
                    <option value="weddings">Weddings</option>
                    <option value="events">Events</option>
                    <option value="matric-balls">Matric Balls</option>
                    <option value="graduations">Graduations</option>
                    <option value="sports">Sports</option>
                  </select>
                </div>
                <div>
                  <label className="label-mono opacity-60">Date / Year</label>
                  <input className="editorial-input" value={selected.date_or_year || ""} onChange={(e) => setSelected({ ...selected, date_or_year: e.target.value })} />
                </div>
                <div>
                  <label className="label-mono opacity-60">Location</label>
                  <input className="editorial-input" value={selected.location || ""} onChange={(e) => setSelected({ ...selected, location: e.target.value })} />
                </div>
                <div>
                  <label className="label-mono opacity-60">Order</label>
                  <input type="number" className="editorial-input" value={selected.order ?? 0} onChange={(e) => setSelected({ ...selected, order: Number(e.target.value) })} />
                </div>
              </div>
              <div className="mt-4">
                <label className="label-mono opacity-60">Description</label>
                <textarea rows={3} className="editorial-textarea" value={selected.description || ""} onChange={(e) => setSelected({ ...selected, description: e.target.value })} />
              </div>

              <div className="mt-6">
                <label className="label-mono opacity-60">Cover image</label>
                <div className="flex items-center gap-4 mt-2">
                  {selected.cover_image && (
                    <img src={mediaUrl(selected.cover_image)} className="w-32 h-24 object-cover" alt="cover" />
                  )}
                  <input type="file" accept="image/*" onChange={(e) => onCoverFile(e.target.files[0])} data-testid="project-cover-upload" />
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <label className="label-mono opacity-60">Gallery images</label>
                  <label className="label-mono cursor-pointer hover-underline">
                    + Upload
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onGalleryFiles(e.target.files)} data-testid="project-gallery-upload" />
                  </label>
                </div>
                {uploading && <p className="label-mono opacity-60 mt-2">Uploading…</p>}
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {(selected.gallery || []).map((g, i) => (
                    <div key={i} className="relative">
                      <img src={mediaUrl(g)} className="w-full h-24 object-cover" alt="" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImg(i)}
                        className="absolute top-1 right-1 bg-black/70 text-white text-xs px-2"
                        data-testid={`project-gallery-remove-${i}`}
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-4 items-center">
                <label className="flex items-center gap-2 label-mono">
                  <input type="checkbox" checked={!!selected.featured} onChange={(e) => setSelected({ ...selected, featured: e.target.checked })} /> Featured
                </label>
                <label className="flex items-center gap-2 label-mono">
                  <input type="checkbox" checked={!!selected.published} onChange={(e) => setSelected({ ...selected, published: e.target.checked })} /> Published
                </label>
              </div>

              <div className="mt-8 flex gap-3">
                <button onClick={save} className="btn-solid" data-testid="project-save">Save</button>
                <a href={`/portfolio/${selected.slug}`} target="_blank" rel="noreferrer" className="btn-outline-ink">Preview →</a>
                <button onClick={del} className="btn-outline-ink text-red-700" data-testid="project-delete">Delete</button>
              </div>
            </div>
          ) : (
            <p className="label-mono opacity-60">Select a project.</p>
          )}
        </div>
      </div>
    </div>
  );
}
